/**
 * 按运行环境跳转：App / 小程序 / H5 iframe / 纯 H5
 */
import { isEmbeddedIframe } from '@/embed-base'
import { isMiniProgramSync } from '@/common/mini-tab-nav.js'

export const RUNTIME_ENV = {
  APP: 'app',
  MINIPROGRAM: 'miniprogram',
  IFRAME: 'iframe',
  H5: 'h5',
}

export function getNewH5Origin() {
  const origin = (process.env.VUE_APP_H5_URL || '').trim().replace(/\/$/, '')
  return origin
}

export function isInApp() {
  if (typeof window === 'undefined') return false
  try {
    if (
      window.plus ||
      window.__dcloud_weex_ ||
      window.__dcloud_weex_postMessage ||
      window.__uniapp_x_ ||
      window.__uniapp_x_postMessage
    ) {
      return true
    }
  } catch (e) {}
  const ua = String(navigator.userAgent || '')
  return /Html5Plus|html5plus/i.test(ua)
}

export function isInMiniProgram() {
  return isMiniProgramSync()
}

export function isInH5Iframe() {
  return isEmbeddedIframe()
}

/** 当前运行环境：app | miniprogram | iframe | h5 */
export function getRuntimeEnv() {
  if (isInApp()) return RUNTIME_ENV.APP
  if (isInMiniProgram()) return RUNTIME_ENV.MINIPROGRAM
  if (isInH5Iframe()) return RUNTIME_ENV.IFRAME
  return RUNTIME_ENV.H5
}

function normalizeNativePath(path) {
  if (!path) return ''
  return path.startsWith('/') ? path : `/${path}`
}

function postNavigateToHost(nativePath) {
  const payload = { type: 'NAVIGATE', payload: { url: nativePath } }
  try {
    window.parent.postMessage(payload, '*')
  } catch (e) {}
  try {
    if (typeof window.uni !== 'undefined' && typeof window.uni.postMessage === 'function') {
      window.uni.postMessage({ data: payload })
    }
  } catch (e) {}
}

function navigateInMiniProgram(nativePath) {
  if (typeof wx === 'undefined' || !wx.miniProgram || typeof wx.miniProgram.navigateTo !== 'function') {
    return false
  }
  const separator = nativePath.indexOf('?') !== -1 ? '&' : '?'
  const currentUrl = encodeURIComponent(window.location.href)
  try {
    wx.miniProgram.postMessage({
      data: { type: 'H5_FORM_URL', fromUrl: currentUrl },
    })
  } catch (e) {}
  wx.miniProgram.navigateTo({
    url: nativePath + separator + 'fromUrl=' + currentUrl,
  })
  return true
}

function navigateInApp(nativePath) {
  try {
    if (typeof window.uni !== 'undefined' && typeof window.uni.navigateTo === 'function') {
      window.uni.navigateTo({ url: nativePath })
      return true
    }
  } catch (e) {}
  postNavigateToHost(nativePath)
  return true
}

function applyLocationHref(href, useTop) {
  if (!href) return false
  const target = useTop && typeof window !== 'undefined' && window.top ? window.top : window
  try {
    target.location.href = href
    return true
  } catch (e) {
    try {
      window.location.href = href
      return true
    } catch (err) {
      return false
    }
  }
}

/**
 * 按环境跳转
 * @param {string} nativePath App / 小程序 / H5 iframe 使用的原生路径，如 /subpages/stars/profile?username=xx
 * @param {{ h5Path?: string, h5Url?: string }} [options]
 *   h5Path: 纯 H5 路径（拼到新版 H5 域名后），默认与 nativePath 相同
 *   h5Url: 纯 H5 完整 URL，优先于 h5Path
 */
export function navigateByEnv(nativePath, options = {}) {
  const path = normalizeNativePath(nativePath)
  if (!path) return false

  const env = getRuntimeEnv()

  if (env === RUNTIME_ENV.APP) {
    return navigateInApp(path)
  }
  if (env === RUNTIME_ENV.MINIPROGRAM) {
    return navigateInMiniProgram(path)
  }
  const fullUrl = options.h5Url || `${getNewH5Origin()}${options.h5Path || path}`

  // iframe：改 top 打开艺人主页，不依赖宿主监听 NAVIGATE
  if (env === RUNTIME_ENV.IFRAME) {
    return applyLocationHref(fullUrl, true)
  }

  return applyLocationHref(fullUrl)
}

/**
 * 跳转明星艺人主页
 * App / 小程序 / H5 iframe → stats-app /subpages/stars/profile
 * 纯 H5 → 新版 H5 域名对应艺人主页
 */
export function navigateToArtistHome(username) {
  const name = username == null ? '' : String(username).trim()
  if (!name) return false
  const qs = `username=${encodeURIComponent(name)}`
  return navigateByEnv(`/subpages/stars/profile?${qs}`)
}
